import { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

const useFloat = (ref, speed, amp, offset) => {
  useFrame(({ clock }) => {
    if (ref.current) ref.current.position.y += (Math.sin(clock.elapsedTime * speed + offset) * amp - (ref.current.position.y - ref.current.userData.baseY)) * 0.08;
  });
};

const Phone = ({ position }) => {
  const ref = useRef();
  useFloat(ref, 1.4, 0.18, 0);
  useFrame(({ clock }) => { if (ref.current) ref.current.rotation.y = Math.sin(clock.elapsedTime * 0.6) * 0.5 - 0.3; });
  return (
    <group ref={ref} position={position} userData={{ baseY: position[1] }} rotation={[0.1, -0.3, 0.08]}>
      <mesh castShadow><boxGeometry args={[1.1, 2.2, 0.12]} /><meshStandardMaterial color="#0f172a" metalness={0.7} roughness={0.25} /></mesh>
      <mesh position={[0, 0, 0.065]}><planeGeometry args={[0.98, 2.05]} /><meshStandardMaterial color="#1d4ed8" emissive="#3b82f6" emissiveIntensity={0.9} /></mesh>
      <mesh position={[0, 0.85, 0.07]}><circleGeometry args={[0.05, 16]} /><meshStandardMaterial color="#020617" /></mesh>
    </group>
  );
};

const Television = ({ position }) => {
  const ref = useRef();
  useFloat(ref, 1.1, 0.14, 2);
  useFrame(({ clock }) => { if (ref.current) ref.current.rotation.y = Math.sin(clock.elapsedTime * 0.5 + 1) * 0.35 + 0.35; });
  return (
    <group ref={ref} position={position} userData={{ baseY: position[1] }}>
      <mesh castShadow><boxGeometry args={[2.6, 1.5, 0.1]} /><meshStandardMaterial color="#111827" metalness={0.6} roughness={0.3} /></mesh>
      <mesh position={[0, 0, 0.055]}><planeGeometry args={[2.45, 1.36]} /><meshStandardMaterial color="#0ea5e9" emissive="#38bdf8" emissiveIntensity={0.7} /></mesh>
      <mesh position={[0, -0.95, 0]}><boxGeometry args={[0.9, 0.08, 0.4]} /><meshStandardMaterial color="#1f2937" metalness={0.8} /></mesh>
    </group>
  );
};

const Fridge = ({ position }) => {
  const ref = useRef();
  useFloat(ref, 0.9, 0.12, 4);
  useFrame(({ clock }) => { if (ref.current) ref.current.rotation.y = Math.sin(clock.elapsedTime * 0.4 + 2) * 0.4 - 0.5; });
  return (
    <group ref={ref} position={position} userData={{ baseY: position[1] }}>
      <mesh castShadow><boxGeometry args={[1.2, 2.4, 1]} /><meshStandardMaterial color="#e2e8f0" metalness={0.9} roughness={0.2} /></mesh>
      <mesh position={[0, 0.35, 0.505]}><planeGeometry args={[1.15, 0.02]} /><meshStandardMaterial color="#94a3b8" /></mesh>
      <mesh position={[0.45, 0.1, 0.52]}><boxGeometry args={[0.06, 0.6, 0.04]} /><meshStandardMaterial color="#334155" metalness={0.9} /></mesh>
      <mesh position={[0.45, -0.6, 0.52]}><boxGeometry args={[0.06, 0.9, 0.04]} /><meshStandardMaterial color="#334155" metalness={0.9} /></mesh>
    </group>
  );
};

const Orbs = () => {
  const ref = useRef();
  useFrame(({ clock }) => { if (ref.current) ref.current.rotation.y = clock.elapsedTime * 0.25; });
  return (
    <group ref={ref}>
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(a) * 3.4, Math.sin(a * 2) * 0.8, Math.sin(a) * 3.4]}>
            <sphereGeometry args={[0.12, 16, 16]} />
            <meshStandardMaterial color={i % 2 ? "#3b82f6" : "#e2e8f0"} emissive={i % 2 ? "#2563eb" : "#000"} emissiveIntensity={0.6} />
          </mesh>
        );
      })}
    </group>
  );
};

const Scene = () => {
  const group = useRef();
  useFrame(({ pointer }) => {
    if (!group.current) return;
    group.current.rotation.y += (pointer.x * 0.35 - group.current.rotation.y) * 0.05;
    group.current.rotation.x += (-pointer.y * 0.2 - group.current.rotation.x) * 0.05;
  });
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[4, 6, 5]} intensity={1.6} castShadow />
      <pointLight position={[-4, 2, 3]} intensity={1.2} color="#60a5fa" />
      <pointLight position={[5, 1, 4]} intensity={1.4} color="#ffffff" />
      <group ref={group}>
        <Phone position={[-2.2, 0.2, 0.6]} />
        <Television position={[0.2, 0.9, -0.6]} />
        <Fridge position={[2.3, -0.4, 0.2]} />
        <Orbs />
      </group>
    </>
  );
};

export const Hero3D = () => (
  <div className="absolute inset-0" data-testid="hero-3d-canvas">
    <Canvas shadows dpr={[1, 1.5]} camera={{ position: [0, 0.4, 7.5], fov: 42 }} gl={{ alpha: true, antialias: true }}>
      <Suspense fallback={null}><Scene /></Suspense>
    </Canvas>
  </div>
);
